import React, { useState, useEffect } from "react";
import {
  FaLock,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaSignInAlt,
  FaShieldAlt,
  FaArrowLeft,
  FaExclamationTriangle,
} from "react-icons/fa";
import { loginAdmin, getAdminUserData } from "../firebase";
import {
  setAdminSession,
  trackLoginAttempt,
  isAccountLocked,
  getRemainingLockoutTime,
  clearLoginAttempts,
  logAdminActivity,
  generateCSRFToken,
  is2FARemembered,
} from "../utils/adminSecurity";
import { is2FAEnabled } from "../utils/twoFactorAuth";
import "./AdminLogin.css";

const AdminLogin = ({ onLoginSuccess, onBackToHome, onRequire2FA }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [csrfToken, setCsrfToken] = useState("");

  // Generate CSRF token on component mount
  useEffect(() => {
    const token = generateCSRFToken();
    setCsrfToken(token);

    // Login page access logging removed - not critical
  }, []);

  // Check account lockout status
  useEffect(() => {
    if (formData.email) {
      const locked = isAccountLocked(formData.email);
      setIsLocked(locked);
      if (locked) {
        const remaining = getRemainingLockoutTime(formData.email);
        setLockoutTime(remaining);
      }
    }
  }, [formData.email]);

  // Update lockout timer
  useEffect(() => {
    let timer;
    if (isLocked && lockoutTime > 0) {
      timer = setInterval(() => {
        const remaining = getRemainingLockoutTime(formData.email);
        setLockoutTime(remaining);
        if (remaining === 0) {
          setIsLocked(false);
        }
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isLocked, lockoutTime, formData.email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");

    // Check lockout status when email changes
    if (name === "email" && value) {
      const locked = isAccountLocked(value);
      setIsLocked(locked);
      if (locked) {
        const remaining = getRemainingLockoutTime(value);
        setLockoutTime(remaining);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if account is locked
    if (isAccountLocked(formData.email)) {
      const remaining = getRemainingLockoutTime(formData.email);
      setError(
        `Hesap geçici olarak kilitlendi. ${remaining} dakika sonra tekrar deneyin.`
      );
      setIsLocked(true);
      setLockoutTime(remaining);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Log login attempt
      logAdminActivity("login_attempt", {
        email: formData.email,
        timestamp: new Date().toISOString(),
        success: false, // Will be updated on success
      });

      const user = await loginAdmin(formData.email, formData.password);

      // Get user data from Firebase to check 2FA status
      const userData = await getAdminUserData(user.uid);
      const requires2FA = userData.twoFactorEnabled;

      if (requires2FA) {
        // Check if 2FA is remembered for this user
        if (is2FARemembered(formData.email, user.uid)) {
          // 2FA is remembered, skip verification
          logAdminActivity("admin_login", {
            email: formData.email,
            timestamp: new Date().toISOString(),
            uid: user.uid,
            method: "2fa_remembered",
          });

          // Complete login with session (include role data)
          setAdminSession(user, userData);
          clearLoginAttempts(formData.email);
          onLoginSuccess(user);
        } else {
          // 2FA is enabled, require verification (don't log yet, wait for 2FA success)
          // Pass user data to 2FA verification
          onRequire2FA(user, formData.email, userData);
        }
      } else {
        // 2FA is NOT enabled - FORCE 2FA SETUP (mandatory)
        // Don't log yet, wait for 2FA setup completion
        // Force 2FA setup - no direct access to admin panel
        onRequire2FA(user, formData.email, userData);
      }
    } catch (error) {
      console.error("Login failed:", error);

      // Track failed login attempt
      const attempts = trackLoginAttempt(formData.email);

      // Log failed login attempt
      logAdminActivity("login_failed", {
        email: formData.email,
        timestamp: new Date().toISOString(),
        error: error.code,
        attemptCount: attempts.count,
      });

      // Handle specific Firebase auth errors
      let errorMessage = "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.";

      // Check if account should be locked after this attempt
      if (isAccountLocked(formData.email)) {
        const remaining = getRemainingLockoutTime(formData.email);
        errorMessage = `Çok fazla başarısız deneme! Hesap ${remaining} dakika süreyle kilitlendi.`;
        setIsLocked(true);
        setLockoutTime(remaining);
      } else {
        const remainingAttempts = 3 - attempts.count;
        if (remainingAttempts > 0) {
          errorMessage += ` ${remainingAttempts} deneme hakkınız kaldı.`;
        }

        switch (error.code) {
          case "auth/user-not-found":
            errorMessage =
              "Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.";
            break;
          case "auth/wrong-password":
            errorMessage = "Şifre hatalı. Lütfen tekrar deneyin.";
            if (remainingAttempts > 0) {
              errorMessage += ` ${remainingAttempts} deneme hakkınız kaldı.`;
            }
            break;
          case "auth/invalid-email":
            errorMessage = "Geçersiz e-posta adresi.";
            break;
          case "auth/too-many-requests":
            errorMessage =
              "Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin.";
            break;
          case "auth/network-request-failed":
            errorMessage =
              "Ağ bağlantısı hatası. İnternet bağlantınızı kontrol edin.";
            break;
          default:
            errorMessage =
              "Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.";
        }
      }

      setError(errorMessage);
    }

    setIsLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="admin-login">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <button className="back-button" onClick={onBackToHome}>
            <FaArrowLeft />
            Ana Sayfaya Dön
          </button>
        </div>

        <div className="admin-login-content">
          <div className="login-form-container">
            <div className="login-header">
              <div className="login-icon">
                <FaShieldAlt />
              </div>
              <h2>Admin Panel Girişi</h2>
              <p>Yönetim paneline erişmek için giriş yapın</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              {/* CSRF Token (hidden) */}
              <input type="hidden" name="csrf_token" value={csrfToken} />

              {/* Account Locked Warning */}
              {isLocked && (
                <div className="lockout-message">
                  <FaExclamationTriangle className="lockout-icon" />
                  <div className="lockout-content">
                    <h4>Hesap Geçici Olarak Kilitli</h4>
                    <p>
                      Çok fazla başarısız giriş denemesi nedeniyle hesabınız
                      kilitlenmiştir.
                    </p>
                    <p className="lockout-timer">
                      Kalan süre: <strong>{lockoutTime} dakika</strong>
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className={`error-message ${isLocked ? "locked" : ""}`}>
                  <FaLock className="error-icon" />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">E-posta Adresi</label>
                <div className="input-container">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="admin@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Şifre</label>
                <div className="input-container">
                  <FaLock className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className={`login-button ${isLocked ? "locked" : ""}`}
                disabled={
                  isLoading || !formData.email || !formData.password || isLocked
                }
              >
                <FaSignInAlt className="button-icon" />
                {isLocked
                  ? `Hesap Kilitli (${lockoutTime}dk)`
                  : isLoading
                  ? "Giriş Yapılıyor..."
                  : "Giriş Yap"}
              </button>
            </form>

            <div className="login-footer">
              <div className="security-note">
                <FaShieldAlt className="security-icon" />
                <div className="security-text">
                  <p>
                    <strong>Güvenlik Uyarısı:</strong> Bu alan sadece yetkili
                    personel içindir.
                  </p>
                  <ul className="security-features-list">
                    <li>Tüm giriş denemeleri kayıt altına alınmaktadır</li>
                    <li>Başarısız girişlerde hesap geçici olarak kilitlenir</li>
                    <li>Session süresi sınırlıdır ve otomatik sonlanır</li>
                    <li>Tüm admin aktiviteleri izlenir</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="login-sidebar">
            <div className="sidebar-content">
              <h3>Güvenli Erişim</h3>
              <div className="security-features">
                <div className="security-item">
                  <FaShieldAlt className="security-item-icon" />
                  <div>
                    <h4>Firebase Authentication</h4>
                    <p>Endüstri standardı güvenlik</p>
                  </div>
                </div>
                <div className="security-item">
                  <FaLock className="security-item-icon" />
                  <div>
                    <h4>Şifrelenmiş Bağlantı</h4>
                    <p>Verileriniz güvende</p>
                  </div>
                </div>
                <div className="security-item">
                  <FaEye className="security-item-icon" />
                  <div>
                    <h4>Erişim Kontrolü</h4>
                    <p>Sadece yetkili kullanıcılar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
